import React, { useEffect, useState, lazy, createElement } from 'react';
import { Calendar, Video, CheckCircle2, BookOpen, MessageSquare, ArrowRight } from 'lucide-react';
import { Button } from '../components/UI/Button';
import { Card } from '../components/UI/Card';
import { PaystackPayment } from '../components/Payment/PaystackPayment';
import { LazySection } from '../components/UI/LazySection';
export const AIClass = () => {
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  // Eagerly load Paystack script when this page mounts
  useEffect(() => {
    if (document.querySelector('script[src*="paystack"]')) return;
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);
  const useCases = ['Drafting professional emails and correspondence', 'Summarizing long documents and reports instantly', 'Creating business plans and proposals', 'Learning new skills and concepts quickly', 'Organizing schedules and daily tasks', 'Generating ideas for marketing and content', 'Translating languages for business or travel', 'Improving writing tone and grammar', 'Analyzing data and creating simple spreadsheets', 'Preparing for interviews or difficult conversations'];
  const curriculum = [{
    title: 'Module 1: The Foundations',
    topics: ['Introduction to AI and ChatGPT', 'The Art of Prompt Engineering', 'Setting up your AI workspace']
  }, {
    title: 'Module 2: Professional Communication',
    topics: ['Writing emails that get responses', 'Adjusting tone for different audiences', 'Drafting reports and summaries']
  }, {
    title: 'Module 3: Daily Productivity',
    topics: ['Automating repetitive tasks', 'Research and information gathering', 'Brainstorming and ideation']
  }, {
    title: 'Module 4: Practical Application',
    topics: ['Live demonstrations', 'Hands-on exercises', 'Interactive Q&A session']
  }];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePaymentSuccess = (reference: string) => {
    // Redirect directly to WhatsApp group after successful payment
    window.location.href = 'https://chat.whatsapp.com/HKWPSPrraU34ebUaCZNBaC?mode=gi_t';
  };
  return <div className="min-h-screen bg-white" data-id="element-1513">
      {/* Hero Section */}
      <section className="bg-gray-50 py-16 md:py-24 border-b border-gray-200" data-id="element-1514">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-1515">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1516">
            <div data-id="element-1517">
              <div className="inline-block px-4 py-1.5 bg-blue-100 text-blue-800 font-semibold rounded-full text-sm mb-6" data-id="element-1518">
                Virtual Masterclass
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1519">
                Master Everyday AI Skills for Professionals
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1520">
                A practical, jargon-free training designed specifically for
                adult learners to integrate AI into their daily work and life.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 mb-8" data-id="element-1521">
                <div className="flex items-center text-gray-700" data-id="element-1522">
                  <Calendar className="w-6 h-6 mr-3 text-blue-600" data-id="element-1523" />
                  <div data-id="element-1524">
                    <p className="font-semibold" data-id="element-1525">April 4th, 2024</p>
                    <p className="text-sm text-gray-500" data-id="element-1526">Save the date</p>
                  </div>
                </div>
                <div className="flex items-center text-gray-700" data-id="element-1527">
                  <Video className="w-6 h-6 mr-3 text-blue-600" data-id="element-1528" />
                  <div data-id="element-1529">
                    <p className="font-semibold" data-id="element-1530">Google Meet</p>
                    <p className="text-sm text-gray-500" data-id="element-1531">
                      Link provided after registration
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Registration Card */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100" data-id="element-1532">
              <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1533">
                Secure Your Spot
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-1534">
                Registration fee:{' '}
                <span className="font-bold text-gray-900" data-id="element-1535">₦3,500</span>
              </p>

              <div className="space-y-4 mb-6" data-id="element-1536">
                <div data-id="element-1537">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1538">
                    Full Name
                  </label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="John Doe" data-id="element-1539" />
                </div>
                <div data-id="element-1540">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1541">
                    Email Address
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="john@example.com" data-id="element-1542" />
                </div>
                <div data-id="element-1543">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1544">
                    Phone Number
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="08012345678" data-id="element-1545" />
                </div>
              </div>

              {formData.name && formData.email && formData.phone ? <PaystackPayment amount={3500} email={formData.email} name={formData.name} phone={formData.phone} onSuccess={handlePaymentSuccess} data-id="element-1546" /> : <Button className="w-full opacity-50 cursor-not-allowed" disabled data-id="element-1547">
                  Fill details to pay ₦3,500
                </Button>}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section — lazy rendered when scrolled near */}
      <LazySection minHeight="400px" rootMargin="300px" data-id="element-1548">
        <section className="py-16 md:py-24" data-id="element-1549">
          <div className="container mx-auto px-4 max-w-6xl" data-id="element-1550">
            <div className="text-center mb-16" data-id="element-1551">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1552">
                Why You Need This Class
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1553">
                10 practical ways you will use AI immediately after this
                training.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-1554">
              {useCases.map((useCase, index) => <div key={index} className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1555">
                  <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1556" />
                  <span className="text-gray-700 font-medium" data-id="element-1557">{useCase}</span>
                </div>)}
            </div>
          </div>
        </section>
      </LazySection>

      {/* Curriculum Section — lazy rendered when scrolled near */}
      <LazySection minHeight="400px" rootMargin="300px" data-id="element-1558">
        <section className="py-16 md:py-24 bg-gray-50 border-t border-gray-200" data-id="element-1559">
          <div className="container mx-auto px-4 max-w-6xl" data-id="element-1560">
            <div className="text-center mb-16" data-id="element-1561">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1562">
                Course Curriculum
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1563">
                A structured, easy-to-follow learning path starting from the
                absolute basics.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1564">
              {curriculum.map((module, index) => <Card key={index} className="p-6 border-t-4 border-t-blue-600" data-id="element-1565">
                  <div className="flex items-center mb-4" data-id="element-1566">
                    <BookOpen className="w-6 h-6 text-blue-600 mr-3" data-id="element-1567" />
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-1568">
                      {module.title}
                    </h3>
                  </div>
                  <ul className="space-y-3" data-id="element-1569">
                    {module.topics.map((topic, tIndex) => <li key={tIndex} className="flex items-start" data-id="element-1570">
                        <ArrowRight className="w-4 h-4 text-gray-400 mr-2 mt-1 flex-shrink-0" data-id="element-1571" />
                        <span className="text-gray-600" data-id="element-1572">{topic}</span>
                      </li>)}
                  </ul>
                </Card>)}
            </div>
          </div>
        </section>
      </LazySection>
    </div>;
};